import React, { useCallback, useMemo } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import PropTypes from 'prop-types';
import { CSSTransition, Transition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { BasicButton } from '../../styles/button';
import { BasicModal } from '../../styles/modal';
import { Component as ByoImageProgressBar } from '../ByoImageProgressBar';
import { useByoContext } from '../ByoProvider';
import ToastMessages from '../ToastMessages/ToastMessages';

const FLOATING_BAR_DURATION = 300;
const TOP_BAR_EXIT_DURATION = 1000;
const TOP_BAR_EXIT_DELAY = 1000;
const MODAL_DELAY = 1500;

const fadeInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(0, 100%, 0)
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0)
  }
`;

const fadeOutAnimation = keyframes`
  0% {
    opacity: 1;
    transform: translate3d(0, 0, 0)
  }
  100% {
    opacity: 0;
    transform: translate3d(0, 100%, 0)
  }
`;

const dialogStyle = css`
  width: 100%;
  border-radius: 0 0 8px 8px;
  box-shadow: 0px -2px 8px 0px ${({ theme }) => theme.colors.lightShadow1};
  padding: ${({ theme }) => theme.spacing.small}px;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  ${props =>
    props.isFloatingBarVisible
      ? `z-index: ${props.theme.zIndex.floatingBar}`
      : null};

  &.bar-exit {
    opacity: 1;
  }

  &.bar-exit-active {
    opacity: 0;
    transition: opacity ${TOP_BAR_EXIT_DURATION}ms;
    transition-delay: ${TOP_BAR_EXIT_DELAY}ms;
  }
`;

const ProgressBarModal = styled(BasicModal).attrs({
  springConfig: {
    tension: 230,
  },
  dialogStyle,
  transitionDelay: MODAL_DELAY,
  transitionFrom: {
    opacity: 0,
    transform: 'translate3d(0, -100%, 0)',
  },
  transitionEnter: {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
  },
  transitionLeave: {
    opacity: 0,
    transform: 'translate3d(0, -100%, 0)',
  },
})`
  ${BasicModal.Wrapper} {
    display: flex;
    align-items: flex-start;
  }
`;

const ModalTitleContainer = styled.div``;

const TopBarHeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100vw;
  background-image: linear-gradient(
    136.94deg,
    ${({ theme }) => theme.colors.electricPurple12} 15.62%,
    ${({ theme }) => theme.colors.redViolet12} 100%
  );
`;

const TopBarHeader = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  position: relative;
  padding: ${({ theme }) => theme.spacing.small}px 28px;
  margin: ${({ theme }) => theme.spacing.small}px 0;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 2px 12px ${({ theme }) => theme.colors.lightShadow1};
  border-bottom: 4px solid ${({ theme }) => theme.colors.active};
`;

const TopBarLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const VerticalLine = styled.div`
  height: 80px;
  margin: 0 ${({ theme }) => theme.spacing.large}px;
  border-left: 1px solid ${({ theme }) => theme.colors.subdued};
`;

const TopBarRightContainer = styled(TopBarLeftContainer)``;

const FloatingProgressBar = styled(TopBarHeader)`
  margin: 0;
  position: fixed;
  bottom: ${({ theme }) => theme.spacing.medium}px;
  padding: ${({ theme }) => theme.spacing.small}px
    ${({ theme }) => theme.spacing.moderate}px;
  z-index: ${({ theme }) => theme.zIndex.floatingBar};

  ${props =>
    (props.state === 'entering' || props.state === 'entered') &&
    css`
      animation: ${fadeInAnimation} ${FLOATING_BAR_DURATION}ms ease-in-out;
    `}

  ${props =>
    (props.state === 'exiting' || props.state === 'exited') &&
    css`
      animation: ${fadeOutAnimation} ${FLOATING_BAR_DURATION}ms ease-in-out;
    `}
`;

const ModalContainerDesktop = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${({ theme }) => theme.spacing.small}px;
`;

const ModalTextContainer = styled(TextContainer)`
  align-items: center;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NoThanksCta = styled(BasicButton)`
  background: ${({ theme }) => theme.colors.white};
  padding: 0px ${({ theme }) => theme.spacing.small}px;
  color: ${({ theme }) => theme.colors.default};
  border-color: ${({ theme }) => theme.colors.white};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1.3px;
  font-family: 'Plain';
  line-height: 17px;
  margin-top: ${({ theme }) => theme.spacing.small}px;
  margin-bottom: 21px;
`;

const MiddleText = styled.p`
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

export default function ByoStickyProgressBarDesktop({
  buildYourSetTitle,
  closeButton,
  confirmationText,
  guideText,
  onExitButtonClick,
  interstitialTitleText,
  isGridPage,
  modalCTA,
}) {
  const {
    currentStep,
    isExitModalOpen,
    isInterstitialFlow,
    isProgressHeaderVisible,
    isProgressBarModalOpen,
    isSetCompleted,
    setIsProgressHeaderVisible,
  } = useByoContext();

  const handleIntersection = useCallback(
    e => {
      setIsProgressHeaderVisible(e.isIntersecting);
    },
    [setIsProgressHeaderVisible]
  );

  const progressBarModalForDesktop = useMemo(
    () => (
      <ProgressBarModal
        isOpen={isProgressBarModalOpen}
        onExit={onExitButtonClick}
        overlayClickExits={false}
        titleId="progress_bar_modal_desktop"
      >
        <ModalContainerDesktop>
          <ByoImageProgressBar imageWidth={110} isTextVisible />
          <ModalTextContainer>
            <ModalTitleContainer id="progress_bar_modal_desktop">
              {buildYourSetTitle}
            </ModalTitleContainer>
            {guideText}
            {modalCTA}
          </ModalTextContainer>
        </ModalContainerDesktop>
      </ProgressBarModal>
    ),
    [
      buildYourSetTitle,
      guideText,
      onExitButtonClick,
      isProgressBarModalOpen,
      modalCTA,
    ]
  );

  const progressBarForDesktop = useMemo(
    () =>
      isGridPage ? (
        <ContentContainer>
          <TopBarHeader>
            {closeButton}
            <TopBarLeftContainer>
              {interstitialTitleText}
              {guideText}
            </TopBarLeftContainer>
            <VerticalLine />
            <TopBarRightContainer>
              <ByoImageProgressBar imageWidth={90} isTextVisible />
            </TopBarRightContainer>
          </TopBarHeader>
          {isInterstitialFlow && currentStep <= 2 ? (
            <>
              <MiddleText>
                <FormattedMessage
                  id="site_byo.progress_modal_middle_text"
                  defaultMessage="OR"
                />
              </MiddleText>
              <NoThanksCta onClick={onExitButtonClick}>
                <FormattedMessage
                  id="site_byo.byo_interstitial_no_thanks_cta"
                  defaultMessage="No thanks, Checkout"
                />
              </NoThanksCta>
            </>
          ) : null}
        </ContentContainer>
      ) : (
        <TopBarHeader>
          {closeButton}
          <ByoImageProgressBar imageWidth={86} isTextVisible />
          <TextContainer>
            {isProgressBarModalOpen || isSetCompleted
              ? confirmationText
              : guideText}
          </TextContainer>
          {/* when exit modal shows up, we want to hide progress bar modal to avoid multiple modal show at the same time */}
          {isExitModalOpen ? null : progressBarModalForDesktop}
        </TopBarHeader>
      ),
    [
      closeButton,
      confirmationText,
      currentStep,
      guideText,
      interstitialTitleText,
      isExitModalOpen,
      isGridPage,
      isInterstitialFlow,
      isProgressBarModalOpen,
      isSetCompleted,
      onExitButtonClick,
      progressBarModalForDesktop,
    ]
  );

  const floatingProgressBar = useCallback(
    transitionState => {
      return !isProgressBarModalOpen ? (
        <FloatingProgressBar
          state={transitionState}
          data-autotag="sticky_progress_bar_desktop"
          data-testid="byo-floating-progress-bar"
        >
          {closeButton}
          <ByoImageProgressBar imageWidth={80} isTextVisible />
          <TextContainer>
            {isSetCompleted ? confirmationText : guideText}
          </TextContainer>
        </FloatingProgressBar>
      ) : null;
    },
    [
      closeButton,
      confirmationText,
      guideText,
      isProgressBarModalOpen,
      isSetCompleted,
    ]
  );

  return (
    <CSSTransition
      classNames="bar"
      in={!isSetCompleted}
      timeout={TOP_BAR_EXIT_DURATION}
      unmountOnExit
    >
      <Wrapper isFloatingBarVisible={!isProgressHeaderVisible}>
        <Observer onChange={handleIntersection} rootMargin="0px 0px 0px 0px">
          <TopBarHeaderContainer data-autotag="sticky_progress_bar_desktop">
            {progressBarForDesktop}
          </TopBarHeaderContainer>
        </Observer>
        <ToastMessages />

        <Transition
          in={!isProgressHeaderVisible}
          appear={!isProgressHeaderVisible}
          timeout={FLOATING_BAR_DURATION}
          mountOnEnter
          unmountOnExit
        >
          {state => floatingProgressBar(state)}
        </Transition>
      </Wrapper>
    </CSSTransition>
  );
}

ByoStickyProgressBarDesktop.propTypes = {
  buildYourSetTitle: PropTypes.node,
  closeButton: PropTypes.node,
  confirmationText: PropTypes.node,
  guideText: PropTypes.node,
  onExitButtonClick: PropTypes.func,
  interstitialTitleText: PropTypes.node,
  isGridPage: PropTypes.bool,
  modalCTA: PropTypes.node,
};
